import { useEffect, useRef, useState } from "react";

const CustomCursor = () => {
  const dotRef = useRef(null);
  const labelRef = useRef(null);
  const trailRef = useRef(null);
  const [variant, setVariant] = useState("default");
  const [label, setLabel] = useState("");
  const variantRef = useRef("default");
  const labelTextRef = useRef("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let curX = mouseX;
    let curY = mouseY;
    let rafId;
    let lastEmit = 0;
    let lastSampleX = mouseX;
    let lastSampleY = mouseY;

    const emitParticle = (x, y) => {
      const layer = trailRef.current;
      if (!layer) return;
      const p = document.createElement("span");
      const size = 5 + Math.random() * 5;
      const hueShift = Math.random() * 30 - 15;
      p.className = "nn-cursor-trail-particle";
      p.style.left = `${x}px`;
      p.style.top = `${y}px`;
      p.style.width = `${size}px`;
      p.style.height = `${size}px`;
      p.style.filter = `hue-rotate(${hueShift}deg)`;
      layer.appendChild(p);
      // remove after animation ends
      window.setTimeout(() => p.remove(), 900);
    };

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      const target = e.target;
      if (target && target.closest) {
        const interactive = target.closest("[data-cursor], a, button, [role='button']");
        const cVar = interactive ? interactive.getAttribute("data-cursor") || "hover" : "default";
        const cLabel = interactive ? interactive.getAttribute("data-cursor-label") || "" : "";
        if (cVar !== variantRef.current) {
          variantRef.current = cVar;
          setVariant(cVar);
        }
        if (cLabel !== labelTextRef.current) {
          labelTextRef.current = cLabel;
          setLabel(cLabel);
        }
      }

      // Throttled particle emission — velocity aware
      if (reduce) return;
      const now = performance.now();
      const dx = mouseX - lastSampleX;
      const dy = mouseY - lastSampleY;
      const dist = Math.hypot(dx, dy);
      if (now - lastEmit >= 22 && dist > 3) {
        lastEmit = now;
        lastSampleX = mouseX;
        lastSampleY = mouseY;
        emitParticle(mouseX, mouseY);
      }
    };

    const tick = () => {
      curX += (mouseX - curX) * 0.35;
      curY += (mouseY - curY) * 0.35;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${curX - 7}px, ${curY - 7}px, 0)`;
      }
      if (labelRef.current) {
        labelRef.current.style.transform = `translate3d(${curX}px, ${curY}px, 0)`;
      }
      rafId = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    rafId = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <>
      <div ref={trailRef} className="nn-cursor-trail" aria-hidden="true" />
      <div ref={dotRef} className={`nn-cursor ${variant}`} data-testid="custom-cursor" />
      <div ref={labelRef} className={`nn-cursor-label ${label ? "show" : ""}`}>
        {label}
      </div>
    </>
  );
};

export default CustomCursor;
